import './defines';
import upload from 'controllers/upload';
import { UploadModel } from 'interfaces/app';
import { PhotoModel } from 'interfaces/project';
import loadImage from 'services/load-image';
import {
	PhotosModule,
	ProductStateModule,
	UploadModule,
} from 'store';
import {
	url as urlTools,
	viewport as viewportTools,
} from 'tools';
import {
	Component,
	Prop,
	Vue,
	Watch,
} from 'vue-property-decorator';
import { MIME_TYPES_BROWSER_SUPPORTED, MIME_TYPES_REQUIRE_CONVERSION } from 'settings/filetypes';
import Template from './template.vue';

@Component({
	name: 'ImportLightboxView',
})
export default class ImportLightboxView extends Vue.extend(Template) {
	@Prop({
		default: true,
		type: Boolean,
	})
	public readonly isModal!: boolean;

	@Prop({
		required: true,
		type: Object,
	})
	public readonly photoModel!: PhotoModel;

	@Prop({
		default: true,
		type: Boolean,
	})
	public readonly showCloseButton!: boolean;

	@Prop({
		default: false,
		type: Boolean,
	})
	public readonly showRemoveButton!: boolean;

	@Prop({
		default: true,
		type: Boolean,
	})
	public readonly showToolsButtons!: boolean;

	@Prop({
		default: false,
		type: Boolean,
	})
	public readonly showSelectButton!: boolean;

	protected get computedStyles(): Partial<CSSStyleDeclaration> {
		const styles: Partial<CSSStyleDeclaration> = {};

		if (
			this.localUrl
			|| this.remoteUrl
		) {
			styles.backgroundImage = `url(${this.localUrl || this.remoteUrl})`;
		}

		let {
			posX,
			posY,
		} = this;

		if (this.displayWidth > this.viewport.width) {
			posX += this.movementX;
		}
		if (this.displayHeight > this.viewport.height) {
			posY += this.movementY;
		}

		styles.backgroundSize = `${this.displayWidth}px ${this.displayHeight}px`;
		styles.backgroundPositionX = `${Math.round(posX)}px`;
		styles.backgroundPositionY = `${Math.round(posY)}px`;

		return styles;
	}

	private get displayHeight(): number {
		return this.photoHeight * this.displayScale;
	}

	private get displayScale(): number {
		return (this.scaleCustom || this.scaleFull) * this.scaleMultiplier;
	}

	private get displayWidth(): number {
		return this.photoWidth * this.displayScale;
	}

	protected get isLoading(): boolean {
		return !!(
			!this.localUrl
			&& !this.remoteUrl
		);
	}

	private get photoHeight(): number {
		return this.photoModel.full_height || this.localHeight;
	}

	private get photoWidth(): number {
		return this.photoModel.full_width || this.localWidth;
	}

	private get posX(): number {
		return (this.viewport.width - this.displayWidth) / 2;
	}

	private get posY(): number {
		return (this.viewport.height - this.displayHeight) / 2;
	}

	private get remoteUrl(): string | null {
		if (
			typeof this.photoModel.id === 'string'
			&& this.photoModel.source === 'upload'
			&& this.photoModel._type
			&& MIME_TYPES_REQUIRE_CONVERSION.includes(this.photoModel._type)
		) {
			return null;
		}

		const imageUrl = this.photoModel.full_url;

		if (
			imageUrl
			&& typeof this.photoModel.id == 'number'
		) {
			// Add CORS busting parameter to URL
			return urlTools.appendParameter(
				imageUrl,
				'noCorsHeader',
			);
		}

		return imageUrl;
	}

	private get scaleFull(): number {
		return Math.min(
			this.viewport.width / this.photoWidth,
			this.viewport.height / this.photoHeight,
		);
	}

	protected get showRetryButton(): boolean {
		if (
			this.showRemoveButton
			&& this.photoModel._error
		) {
			return true;
		}

		return false;
	}

	private get uploadModel(): UploadModel | undefined {
		if (this.photoModel?.externalId) {
			return UploadModule.find(this.photoModel.externalId);
		}

		return undefined;
	}

	private localHeight = 0;

	private localUrl: string | null = null;

	private localWidth = 0;

	private movementX = 0;

	private movementY = 0;

	private scaleCustom = 0;

	private scaleMultiplier = 1;

	private viewport = viewportTools.fullViewport;

	private viewportUnwatch?: () => void;

	protected beforeDestroy() {
		this.viewportUnwatch?.();
	}

	protected created(): void {
		this.viewportUnwatch = viewportTools.watch(
			this.onViewportChange,
			'full',
		);
	}

	protected mounted() {
		this.loadLocalPhoto();
	}

	@Watch('photoModel')
	private loadLocalPhoto() {
		if (this.uploadModel) {
			const localFile = upload.getLocalFile(this.uploadModel.id);

			if (
				localFile
				&& this.photoModel._type
				&& MIME_TYPES_BROWSER_SUPPORTED.includes(this.photoModel._type)
			) {
				loadImage(
					localFile,
					{
						forceCopy: true,
					},
				)
					.then(({ image }) => {
						this.localUrl = image.src;
						this.localWidth = image.width;
						this.localHeight = image.height;
					})
					.catch(() => {
						// Log this filesize error, so we can see how often it occurs
						if (typeof window.glBugsnagClient !== 'undefined') {
							window.glBugsnagClient.notify(
								new Error('Could not load local photo in lightbox'),
							);
						}
					});
			}
		}
	}

	private closeLightBox() {
		this.$emit('closeLightBox');
	}

	protected onPan(hammerEvent: HammerInput) {
		this.movementX = Math.max(
			Math.min(
				(this.photoWidth * this.displayScale - this.viewport.width) / 2,
				this.movementX + hammerEvent.changedPointers[0].movementX,
			),
			(this.viewport.width - this.photoWidth * this.displayScale) / 2,
		);
		this.movementY = Math.max(
			Math.min(
				(this.photoHeight * this.displayScale - this.viewport.height) / 2,
				this.movementY + hammerEvent.changedPointers[0].movementY,
			),
			(this.viewport.height - this.photoHeight * this.displayScale) / 2,
		);
	}

	protected onPinch(hammerEvent: HammerInput) {
		const { scale } = hammerEvent;
		this.scaleMultiplier = scale;

		if (this.displayWidth > this.viewport.width) {
			this.movementX = Math.max(
				(this.viewport.width - this.displayWidth) / 2,
				Math.min(
					(this.displayWidth - this.viewport.width) / 2,
					this.movementX,
				),
			);
		}
		if (this.displayHeight > this.viewport.height) {
			this.movementY = Math.max(
				(this.viewport.height - this.displayHeight) / 2,
				Math.min(
					(this.displayHeight - this.viewport.height) / 2,
					this.movementY,
				),
			);
		}
	}

	protected onPinchEnd(hammerEvent: HammerInput) {
		this.scaleCustom = Math.max(
			this.scaleFull,
			(this.scaleCustom || this.scaleFull) * hammerEvent.scale,
		);
		this.scaleMultiplier = 1;
	}

	private onViewportChange() {
		this.viewport = viewportTools.fullViewport;
		this.movementX = 0;
		this.movementY = 0;
	}

	protected removePhoto() {
		ProductStateModule.removePhoto(this.photoModel.id);
		this.closeLightBox();
	}

	protected retryUpload() {
		if (this.photoModel.full_url) {
			PhotosModule
				.retryModel(this.photoModel.id)
				.catch(() => {
					// Swallow error: no action required
				});
		} else if (
			this.uploadModel
			&& UploadModule.findError(this.uploadModel.id)
		) {
			upload.retry(this.uploadModel);
		}
	}

	protected selectPhoto() {
		this.$emit(
			'selectPhoto',
			this.photoModel,
		);
	}
}
